import React, { useContext, useMemo } from "react";

import { BrowserRouter as Router } from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
  QueryObserverOptions,
} from "@tanstack/react-query";

import useBoardStatus from "./hooks/useBoardStatus";
import { usePersistentQuery } from "../../lib/hooks/usePersistentQuery";

const isDev = process.env.NODE_ENV === "development";

const Status = React.memo(() => {
  useBoardStatus();

  return null;
});

interface AppContext {
  screenId: string;
  clientId: string;
  config: {
    connect: boolean;
    preview: boolean;
  };
}

const AppContext = React.createContext<AppContext>(undefined!);

const { Provider } = AppContext;

export const useAppContext = () => useContext(AppContext);

export const useAppConfig = () => useAppContext().config;

const App = React.memo<{ children: React.ReactNode }>(({ children }) => {
  const query = usePersistentQuery();

  const basename = useMemo(() => {
    if (isDev) {
      return "/";
    }

    return window.location.pathname
      .split("/")
      .filter(Boolean)
      .slice(0, 4)
      .join("/");
  }, []);

  const value = useMemo((): AppContext => {
    const {
      location: { pathname },
    } = window;

    const paths = pathname.split("/");

    const screenId = isDev ? process.env.LOCALE_BOARD_ID : paths[4];

    const clientId = isDev ? process.env.API_CLIENT_TOKEN : paths[2];

    return {
      screenId,
      clientId,
      config: {
        connect: !(query?.connect === "false"),
        preview: !!query?.preview,
      },
    };
  }, [query]);

  const client = useMemo(() => {
    const options: QueryObserverOptions = {
      refetchInterval: 300000,
    };

    const client = new QueryClient({
      defaultOptions: {
        queries: options,
      },
    });

    return client;
  }, []);

  return (
    <Provider value={value}>
      <QueryClientProvider client={client}>
        <Router basename={basename}>
          <Status />
          {children}
        </Router>
      </QueryClientProvider>
    </Provider>
  );
});

export default App;
